import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { DownloadSimple } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL || '';
const STATUS_LABEL = { por_fazer: 'Por fazer', feita: 'Feitas', nao_aplicavel: 'Não aplicáveis', todas: 'Todas' };
const PROGRAMS = [['introducao_alimentar', 'Super Bebés'], ['gravida', 'Super Barriguinhas'], ['pos_parto', 'Super Mamãs'], ['pre_concecao', 'Super Pré Mamãs'], ['emagrecimento', 'Emagrecimento'], ['geral', 'Geral']];
const LEGACY_LABELS = { agendar_consulta: 'Agendar próxima consulta', emitir_fatura: 'Emitir fatura', atualizar_plano: 'Atualizar plano alimentar', renovacao_programa: 'Renovação de programa' };
const fmt = (s) => (s || '').split('-').reverse().join('/');

export function ListView() {
  const { user } = useAuth();
  const teamView = user?.role === 'admin' && localStorage.getItem('team_view') === 'true';
  const [f, setF] = useState({ status: 'por_fazer', nutri_id: 'all', tipo: 'all', program: 'all' });
  const [data, setData] = useState({ consultas: [] });
  const [nutris, setNutris] = useState([]);
  const [legacy, setLegacy] = useState([]);
  const [naModal, setNaModal] = useState({ open: false, id: null, text: '' });
  const params = { status: f.status, nutri_id: teamView && f.nutri_id !== 'all' ? f.nutri_id : undefined, tipo: f.tipo !== 'all' ? f.tipo : undefined, program: f.program !== 'all' ? f.program : undefined };

  const load = useCallback(async () => {
    try { const { data: d } = await axios.get(`${API}/api/consult-tasks/list`, { params }); setData(d); } catch { setData({ consultas: [] }); }
    try { const { data: l } = await axios.get(`${API}/api/auto-tasks`); setLegacy(l || []); } catch { setLegacy([]); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f, teamView]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (teamView) axios.get(`${API}/api/consult-tasks/week`).then(r => setNutris(r.data.nutritionists || [])).catch(() => {}); }, [teamView]);

  const toggle = async (t, status) => { try { await axios.put(`${API}/api/consult-tasks/${t.id}/status`, { status }); load(); } catch { toast.error('Erro ao atualizar tarefa'); } };
  const exportCsv = async () => {
    try {
      const { data: blob } = await axios.get(`${API}/api/consult-tasks/list.csv`, { params, responseType: 'blob' });
      const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = 'tarefas_consultas.csv'; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error('Erro ao exportar'); }
  };
  const submitNa = async () => {
    if (!naModal.text.trim()) { toast.error('A justificação é obrigatória'); return; }
    try { await axios.post(`${API}/api/auto-tasks/${naModal.id}/not-applicable`, { justification: naModal.text }); setNaModal({ open: false, id: null, text: '' }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };
  const sel = (key, items, ph, tid) => (
    <Select value={f[key]} onValueChange={v => setF(s => ({ ...s, [key]: v }))}>
      <SelectTrigger className="h-9 w-40 text-xs" data-testid={tid}><SelectValue placeholder={ph} /></SelectTrigger>
      <SelectContent>{items.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  );

  return (
    <div data-testid="list-view">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {sel('status', Object.entries(STATUS_LABEL), 'Estado', 'list-filter-status')}
        {sel('tipo', [['all', 'Todos os tipos'], ['primeira', '1.ª consulta'], ['seguimento', 'Seguimento']], 'Tipo', 'list-filter-tipo')}
        {sel('program', [['all', 'Todos os programas'], ...PROGRAMS], 'Programa', 'list-filter-program')}
        {teamView && sel('nutri_id', [['all', 'Toda a equipa'], ...nutris.map(n => [n.user_id, n.name])], 'Nutricionista', 'list-filter-nutri')}
        <Button variant="outline" size="sm" className="ml-auto text-xs gap-1" onClick={exportCsv} data-testid="list-export-csv-btn"><DownloadSimple size={14} /> Exportar CSV</Button>
      </div>
      {data.consultas.length === 0 ? <p className="text-sm text-brand-placeholder text-center py-8" data-testid="list-empty">Sem tarefas para mostrar.</p> : (
        <div className="space-y-3">
          {data.consultas.map(g => (
            <div key={g.appointment_id || g.consultation_id} className="bg-white border border-brand-border rounded-2xl p-3.5" data-testid={`list-group-${g.appointment_id || g.consultation_id}`}>
              <div className="flex flex-wrap items-baseline gap-x-2 mb-1">
                <Link to={`/admin/pacientes/${g.patient_id}`} className="text-sm font-medium text-brand-text underline decoration-[#bbd6d5]">{g.patient_name}</Link>
                <span className="text-xs text-brand-muted">{fmt(g.date)} {g.time} · {g.tipo_short}{g.program_label ? ` · ${g.program_label}` : ''}{teamView && g.nutritionist_name ? ` · ${g.nutritionist_name}` : ''}</span>
              </div>
              {g.tasks.map(t => (
                <label key={t.id} className="flex items-center gap-3 min-h-[44px] cursor-pointer" data-testid={`list-task-${t.id}`}>
                  <input type="checkbox" className="w-5 h-5 accent-[#657161]" checked={t.status === 'feita'} disabled={t.status === 'nao_aplicavel'} onChange={e => toggle(t, e.target.checked ? 'feita' : 'por_fazer')} />
                  <span className={`text-sm ${t.status === 'por_fazer' ? 'text-brand-text' : 'text-brand-placeholder line-through'}`}>{t.label}</span>
                  {t.auto && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#bbd6d5]/50 text-[#3f5a58]" title={t.auto_label}>automático</span>}
                </label>
              ))}
            </div>
          ))}
        </div>
      )}
      {legacy.length > 0 && (
        <div className="mt-6" data-testid="legacy-auto-section">
          <h3 className="font-heading text-sm text-brand-text mb-2">Outras automáticas (por paciente)</h3>
          <div className="space-y-2">
            {legacy.map(t => (
              <div key={t.id} className="bg-white border border-brand-border rounded-2xl p-3 flex items-center justify-between gap-3" data-testid={`auto-task-${t.id}`}>
                <div className="min-w-0"><p className="text-sm font-medium text-brand-text">{t.type_label}</p><p className="text-xs text-brand-muted truncate">{t.patient_name} {t.due_context ? `· ${t.due_context}` : ''}</p></div>
                <Button variant="outline" size="sm" className="text-xs shrink-0" onClick={() => setNaModal({ open: true, id: t.id, text: '' })} data-testid={`auto-task-na-btn-${t.id}`}>Não aplicável</Button>
              </div>
            ))}
          </div>
        </div>
      )}
      <Dialog open={naModal.open} onOpenChange={v => setNaModal(m => ({ ...m, open: v }))}>
        <DialogContent className="rounded-3xl border-brand-border" data-testid="not-applicable-modal">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Marcar como não aplicável</DialogTitle></DialogHeader>
          <Textarea placeholder="Justificação (obrigatória)" value={naModal.text} onChange={e => setNaModal(m => ({ ...m, text: e.target.value }))} data-testid="na-justification-input" />
          <Button className="w-full bg-brand-green text-white" onClick={submitNa} data-testid="na-submit-btn">Confirmar</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function ArchiveView() {
  const [data, setData] = useState({ consultas: [], legacy: [] });
  const load = useCallback(async () => { try { const { data: d } = await axios.get(`${API}/api/consult-tasks/archive`); setData(d); } catch { setData({ consultas: [], legacy: [] }); } }, []);
  useEffect(() => { load(); }, [load]);
  const reactivate = async (id, legacy) => {
    try { await axios.post(legacy ? `${API}/api/consult-tasks/legacy/${id}/reactivate` : `${API}/api/consult-tasks/${id}/reactivate`); toast.success('Tarefa reativada'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erro ao reativar'); }
  };
  const empty = data.consultas.length === 0 && data.legacy.length === 0;
  return (
    <div data-testid="archive-view">
      {empty && <p className="text-sm text-brand-placeholder text-center py-8" data-testid="archive-empty">Sem tarefas arquivadas.</p>}
      <div className="space-y-3">
        {data.consultas.map(g => (
          <div key={g.appointment_id || g.consultation_id} className="bg-white border border-brand-border rounded-2xl p-3.5">
            <p className="text-sm font-medium text-brand-text">{g.patient_name} <span className="text-xs text-brand-muted font-normal">· {fmt(g.date)} {g.time} · {g.tipo_short}</span></p>
            {g.tasks.map(t => (
              <div key={t.id} className="flex items-center justify-between gap-2 min-h-[44px]" data-testid={`archive-task-${t.id}`}>
                <span className="text-sm text-brand-muted">{t.label} <span className="text-[10px] text-brand-placeholder">({t.archived_reason})</span></span>
                <Button variant="outline" size="sm" className="text-xs" onClick={() => reactivate(t.id, false)} data-testid={`archive-reactivate-${t.id}`}>Reativar</Button>
              </div>
            ))}
          </div>
        ))}
        {data.legacy.map(t => (
          <div key={t.id} className="bg-white border border-brand-border rounded-2xl p-3 flex items-center justify-between gap-3" data-testid={`archive-legacy-${t.id}`}>
            <div className="min-w-0"><p className="text-sm font-medium text-brand-text">{LEGACY_LABELS[t.type] || t.type}</p><p className="text-xs text-brand-muted truncate">{t.patient_name} {t.due_context ? `· ${t.due_context}` : ''}</p></div>
            <Button variant="outline" size="sm" className="text-xs shrink-0" onClick={() => reactivate(t.id, true)} data-testid={`archive-legacy-reactivate-${t.id}`}>Reativar</Button>
          </div>
        ))}
      </div>
    </div>
  );
}
